import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  selectIsUserExperimentLoaded,
  updateUserExperiment,
} from '../../store/userExperiment'
import { EXPERIMENT_IDS, ExperimentId } from './useExperiment'
import { AllState } from '../../store'
import { useUserExperimentRepository } from '../../services/adaptors/userExperimentRepositoryAdaptor'

type ExperimentSettingValue = {
  experimentId: ExperimentId
  useNew: boolean
}

type UseExperimentSettingsResult = {
  /** 全ての実験設定 */
  settings: ExperimentSettingValue[]
  /** ローカルで編集中の設定値を更新 */
  updateSetting: (experimentId: ExperimentId, useNew: boolean) => void
  /** 変更を保存 */
  save: () => Promise<void>
  /** 未保存の変更があるか */
  isDirty: boolean
  /** 設定がロードされているか */
  isLoaded: boolean
  /** 保存中か */
  isSaving: boolean
}

/**
 * 設定画面用のフック
 * - 全ての実験設定を取得・編集
 * - 明示的な保存（即時保存ではない）
 * - バナー表示対象として登録されない
 */
export const useExperimentSettings = (): UseExperimentSettingsResult => {
  const dispatch = useDispatch()
  const repository = useUserExperimentRepository()
  const isLoaded = useSelector((state: AllState) =>
    selectIsUserExperimentLoaded(state)
  )
  const storeSettings = useSelector(
    (state: AllState) => state.userExperiment.settings
  )

  // ローカル編集用のステート
  const [localSettings, setLocalSettings] = useState<Record<string, boolean>>(
    {}
  )
  const [isSaving, setIsSaving] = useState(false)

  // ストアの値でローカルステートを初期化
  useEffect(() => {
    if (isLoaded) {
      setLocalSettings(storeSettings)
    }
  }, [isLoaded, storeSettings])

  // 全ての実験IDと現在の設定値を結合
  const settings = useMemo((): ExperimentSettingValue[] => {
    return EXPERIMENT_IDS.map(experimentId => {
      // 設定がない場合は新しいUI（useNew = true）がデフォルト
      const useNew = localSettings[experimentId] ?? true
      return { experimentId, useNew }
    })
  }, [localSettings])

  // 未保存の変更があるか
  const isDirty = useMemo(() => {
    return EXPERIMENT_IDS.some(id => {
      const localValue = localSettings[id]
      const storeValue = storeSettings[id]
      // ローカルに値があり、ストアの値と異なる場合は変更あり
      if (localValue !== undefined && localValue !== storeValue) {
        return true
      }
      return false
    })
  }, [localSettings, storeSettings])

  // ローカル設定を更新
  const updateSetting = useCallback(
    (experimentId: ExperimentId, useNew: boolean) => {
      setLocalSettings(prev => ({
        ...prev,
        [experimentId]: useNew,
      }))
    },
    []
  )

  // 変更を保存
  const save = useCallback(async () => {
    // 変更された設定のみ抽出
    const changedSettings = Object.entries(localSettings)
      .filter(
        ([experimentId, useNew]) => storeSettings[experimentId] !== useNew
      )
      .map(([experimentId, useNew]) => ({
        experimentId,
        useNew,
      }))

    if (changedSettings.length === 0) return

    setIsSaving(true)
    try {
      // バッチ更新APIで一括保存
      await repository.batchUpdate(changedSettings)
      // Redux stateを更新
      changedSettings.forEach(({ experimentId, useNew }) => {
        dispatch(updateUserExperiment(experimentId, useNew))
      })
    } finally {
      setIsSaving(false)
    }
  }, [localSettings, storeSettings, repository, dispatch])

  return {
    settings,
    updateSetting,
    save,
    isDirty,
    isLoaded,
    isSaving,
  }
}
